/* complete_options.control_adapter.js */

var $ = require("jquery");
var _ = require("lodash");
var underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
var EventEmitter = require("events").EventEmitter;
var util = require("util");
var q = require("q");

const ComboBoxControl = require("@gluenetworks/combobox").Single;
const StyleUtil = require("../StyleUtil");
/**
 * @event CompleteOptionsControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class CompleteOptionsControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires CompleteOptionsControlAdapter#ValueChanged
 */
module.exports = function CompleteOptionsAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadedDeferred = q.defer();
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    //call Event Emitter Constructor
    EventEmitter.call(this);
    // DOM representation of the chosen components
    var _scope;
    var _options = _getOptionsFromCS();
    var _oldValue;
    let _oldInProgressStatus = false;
    var _valueCount = 0;
    var _renderCount = 0;

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /**
     * @typedef CompleteOption
     * @property label {string} The label to use for this option
     * @property propName {string} The ConfigSettings property name associated with this option
     * @property configSettings {ConfigurationSettings} The configurations settings associated with this option
     * @property currentlyInUse {boolean} If this option is already in use.
     * @property [configData] {ConfigurationData} The configuration data associated with this option
     * @property [refInstances] {RefInstances} The Ref Instances needed for this Forms configurationSettings
     * @property [refTypes] {RefTypes} The Ref Types needed for this Forms configurationSettings
     * @property [renderer] {object}
     * @property [renderer.api] {RendererApi} The renderer API object for this option
     * @property [renderer.settings] {RendererSettings} The renderer API object for this option
     * @property [renderer.elem] {DomElement} The Element that represents this option's renderer
     */

    /**
     * @typedef SelectOption
     * @property label {string} The label to use when rendering this option
     * @property value {CompleteOption} The option this SelectOption represents.
     */

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * Get the available options from the Config Settings
     * @returns {Object<String,CompleteOption>} The possible options for this @Complete.options
     * @private
     */
    function _getOptionsFromCS() {
        var completeOpts = formCtrlDef.configSettings.options;
        var options = {};
        if (_.isArray(completeOpts["@propertyOrder"])) {
            let sortedOptions = completeOpts["@propertyOrder"].slice();
            sortedOptions.sort();
            sortedOptions.forEach(function (optionName) {
                var label = optionName;
                if (completeOpts[optionName].Display && completeOpts[optionName].Display.label) {
                    label = completeOpts[optionName].Display.label;
                }

                //Grab the configuration settings
                var cs = {};
                cs[optionName] = completeOpts[optionName];

                //Grab the config data
                var allConfigData = formCtrlDef.configData;
                var cd;
                if (
                    allConfigData &&
                    _.isObject(allConfigData) &&
                    Object.hasOwnProperty(optionName)
                ) {
                    cd = allConfigData[optionName];
                }
                //generate the new option
                options[optionName] = {
                    propName: optionName,
                    label: label,
                    configSettings: cs,
                    configData: cd,
                    refInstances: formCtrlDef.refInstances,
                    refTypes: formCtrlDef.refTypes,
                    currentlyInUse: false,
                };
            });
        }
        return options;
    }

    /**
     * Get the options in the select options format.
     * @returns {Array<SelectOption>} Array of options in the form {label,value}
     * @private
     */
    function _getCompleteOptionsForMultiSelect(options) {
        var mso = [];
        Object.keys(options).forEach(function (optName) {
            var optionVal = options[optName];
            if (!optionVal.currentlyInUse) {
                mso.push({label: optionVal.label, value: optionVal});
            }
        });
        return mso;
    }

    /**
     * Set the used status of this CompleteOption.
     * @param option {CompleteOption} The option that the used status is being set on.
     * @param inUse {boolean} If the option is in use or not.
     * @private
     */
    function _setUsedStatus(option, inUse) {
        if (_scope) {
            option.currentlyInUse = inUse;
            var notUsed = _.filter(_scope.CompleteOptions._options, {currentlyInUse: false});

            //If there are options Available
            if (_.isEmpty(notUsed)) {
                _scope.CompleteOptions._isOptionsAvailable = false;
            } else {
                _scope.CompleteOptions._isOptionsAvailable = true;
            }
        }
    }

    /**
     * Refreshes the select options based on what is still available
     * @private
     */
    function _refreshSelectOptions() {
        //get available options
        var notUsed = _.filter(_scope.CompleteOptions._options, {currentlyInUse: false});
        var newOpts = _getCompleteOptionsForMultiSelect(notUsed);
        //Set the starting options
        if (_scope.selectList && _.isFunction(_scope.selectList.setOptions)) {
            _scope.selectList.setOptions(newOpts);
        }
    }

    /**
     * Fire the Value Changed Event
     * @private
     */
    function _fireValueChanged() {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    }

    /**
     * Fire the InProgress Status changed event
     * @private
     */
    function _fireInProgressStatusChanged() {
        let newInProgressStatus = self.isInProgress();
        self.emit("InProgress", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldInProgressStatus: _oldInProgressStatus,
            newInProgressStatus: newInProgressStatus,
        });
        _oldInProgressStatus = newInProgressStatus;
    }

    /**
     * Set the initial state of the readOnly.
     * @private
     */
    function _setInitialReadOnlyState() {
        if (self._readOnlyField) {
            self.setReadOnly(true);
        }
        if (self._multiEditMode) {
            self.setReadOnly(true);
        }
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = {};
        Object.keys(_options).forEach(function (optName) {
            var optionVal = _options[optName];
            if (optionVal.currentlyInUse) {
                _.extend(val, optionVal.renderer.api.getValue());
            }
        });
        return val;
    };

    this.isInProgress = function () {
        return Object.keys(_options).some(function (optName) {
            let optionVal = _options[optName];
            return (
                optionVal.currentlyInUse &&
                _.isFunction(optionVal.renderer.api.isInProgress) &&
                optionVal.renderer.api.isInProgress()
            );
        });
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value, silent = true) {
        _renderCount = 0;
        _valueCount = 0;
        Object.keys(_options).forEach(function (optName) {
            if (typeof value[optName] !== "undefined") {
                _valueCount++;
            }
        });
        var valueFound = false;
        Object.keys(_options).forEach(function (optName) {
            var opt = _options[optName];
            //If there is a value associated with this option
            if (typeof value[optName] !== "undefined") {
                valueFound = true;
                opt.configData = value;
                if (opt.renderer) {
                    opt.renderer.api.renderValues(opt.configData, silent);
                } else {
                    _scope.CompleteOptions._renderOption(containingElem, opt);
                }
                _refreshSelectOptions();
            } else {
                _scope.CompleteOptions.closeOption(optName);
            }
        });

        //If the value does not match any "option"
        if (!loadedDeferred.promise.isFulfilled() && !valueFound) {
            _setInitialReadOnlyState();
            loadedDeferred.resolve();
        }

        return self.getValue();
    };

    this.setReadOnly = function (flag) {
        let addLabel = containingElem.find(
            " > .complete-options-selection-container > .complete-options-footer > .complete-options-add"
        );
        let closeItems = containingElem.find(".close_option");
        if (flag) {
            if (addLabel[0]) {
                addLabel[0].setAttribute("disabled", "disabled");
            }
            closeItems.each((index) => {
                closeItems[index].style.visibility = "hidden";
            });
        } else {
            if (addLabel[0]) {
                addLabel[0].removeAttribute("disabled");
            }
            closeItems.each((index) => {
                closeItems[index].removeAttribute("style");
            });
        }

        for (let optionName in _options) {
            let opt = _options[optionName];
            if (_.has(opt, "renderer.api.setReadOnly")) {
                opt.renderer.api.setReadOnly(flag);
            }
        }
    };

    /**
     * Return true if the component is multi edit active.
     * @returns {boolean}
     */
    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            for (let optionName in _options) {
                let opt = _options[optionName];
                if (_.has(opt, "renderer.api.setUnderlingsMultiEditActive")) {
                    opt.renderer.api.setUnderlingsMultiEditActive(flag);
                }
            }
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * Get the path properties that are not currently selected.
     */
    this.getNonSelectedPaths = function (path) {
        let pathExtension = "";
        if (!_.isEmpty(path)) {
            pathExtension = path + ".";
        }
        let nonSelectedPaths = [];
        let notUsed = _.filter(_scope.CompleteOptions._options, {currentlyInUse: false});
        for (let optionName in _options) {
            let opt = _options[optionName];
            if (!opt.currentlyInUse) {
                nonSelectedPaths.push(pathExtension + optionName);
            }
        }
        return nonSelectedPaths;
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        Object.keys(_options).forEach(function (optName) {
            var optionVal = _options[optName];
            if (optionVal.renderer) {
                optionVal.renderer.api.tearDown();
            }
        });
        _scope.selectList.removeListener("UserValue", _scope.selectListChangeHandler);
        _scope.selectList.shutdown();
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    this.$init = function ($scope, $templateCache, $compile, $timeout) {
        _scope = $scope;
        _scope.formCtrlDef = formCtrlDef;

        _scope.selectList = new ComboBoxControl({showFilter: true});
        _scope.selectListChangeHandler = (newVal) => {
            if (newVal) {
                var opt = _options[newVal.propName];
                $scope.CompleteOptions._renderOption(containingElem, opt, true);
            }
            _refreshSelectOptions();
        };
        _scope.selectList.on("UserValue", _scope.selectListChangeHandler);

        //if this is defaulting to a non helpful tooltip go ahead and hide it
        if (
            formCtrlDef.configSettings.options["@Display"] &&
            formCtrlDef.configSettings.options["@Display"].description
        ) {
            formCtrlDef.displayInfo.tooltip =
                formCtrlDef.configSettings.options["@Display"].description;
        } else {
            formCtrlDef.displayInfo.tooltip = "";
        }

        //Setup Settings Object
        $scope.CompleteOptions = {
            _isOptionsAvailable: true,
            _options: _options,
            /**
             * Render an @Complete.options option
             * @param containingElem {DomElement} The dom Element associated with this containing element.
             * @param opt {CompleteOption} The option to render
             * @private
             */
            _renderOption: function (containingElem, opt, selectFirstElem) {
                //Set option to in use
                _setUsedStatus(opt, true);
                opt.renderer = {
                    api: {},
                    settings: {
                        configurationSettings: opt.configSettings,
                        configurationData: opt.configData,
                        refInstances: formCtrlDef.refInstances,
                        refTypes: formCtrlDef.refTypes,
                    },
                };
                var typeAsClassName = _.underscored(
                    $scope.CompleteOptions._options[opt.propName].configSettings[opt.propName]
                        .fieldType
                );
                //Render
                var optionRendererElem = $(
                    "<gl-form-renderer " +
                        'class="complete_option_renderer ' +
                        opt.propName +
                        " " +
                        typeAsClassName +
                        '" ' +
                        "data-api=\"CompleteOptions._options['" +
                        opt.propName +
                        "'].renderer.api\" " +
                        "data-settings=\"CompleteOptions._options['" +
                        opt.propName +
                        "'].renderer.settings\">" +
                        '<i class="fa fa-times close_option" data-ng-click="CompleteOptions.closeOption(\'' +
                        opt.propName +
                        "')\"></i>" +
                        "</gl-form-renderer>"
                );
                // Create a link function by compiling the template
                var linkFn = $compile(optionRendererElem);
                // Create the element with the compiled template with the same scope
                var elem = linkFn($scope);

                var selectOptionsItemsContainerElem = containingElem
                    .children(".complete-options-selection-container")
                    .children(".complete-options-items-container");
                // Append the element to the DOM
                selectOptionsItemsContainerElem.append(elem);

                opt.renderer.elem = elem;

                if (selectFirstElem) {
                    opt.renderer.api.once("AllControlsLoaded", function () {
                        opt.renderer.api.selectFirstElement();
                    });
                }

                //When all the nested controls are loaded
                opt.renderer.api.once("AllControlsLoaded", function (evt) {
                    if (!loadedDeferred.promise.isFulfilled()) {
                        _renderCount++;
                        if (_renderCount >= _valueCount) {
                            _setInitialReadOnlyState();
                            loadedDeferred.resolve();
                        }
                    }
                    _fireValueChanged();
                    _fireInProgressStatusChanged();
                });

                //When the option's value changes - register immediately, not in AllControlsLoaded
                //Store listener reference to prevent duplicate registration
                if (!opt._valueChangedListener) {
                    opt._valueChangedListener = function (/*evt*/) {
                        _fireValueChanged();
                    };
                    opt.renderer.api.on("ValueChanged", opt._valueChangedListener);
                }

                if (!opt._inProgressListener) {
                    opt._inProgressListener = function (/*evt*/) {
                        _fireInProgressStatusChanged();
                    };
                    opt.renderer.api.on("InProgress", opt._inProgressListener);
                }
            },
            /**
             * Close and Destroy a visible option
             * @param optionName {string} The name of the option to tearDown
             */
            closeOption: function (optionName) {
                var option = _options[optionName];
                if (option.renderer) {
                    option.renderer.api.removeAllListeners();
                    option.renderer.api.tearDown();
                    option.renderer.elem.remove();
                    delete option.renderer;
                }
                //Set option to in use
                _setUsedStatus(option, false);
                //Update the select options
                _refreshSelectOptions();
                //Notify the world that the value has changed
                _fireValueChanged();
            },
        };

        //Setup directive
        var template = $templateCache.get("templatecache/templates/complete_options.template.html");
        var completeOptionsElem = $(template);
        containingElem.append(completeOptionsElem);
        let optionsContainer = containingElem.find(".complete-options-selection-container");
        optionsContainer.append(_scope.selectList.renderer.rootElement);

        let stylesConfig = _.get(formCtrlDef, "configSettings.options.@Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this boolean element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });

        return loadedDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache", "$compile", "$timeout"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope) {
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
        }

        if (
            formCtrlDef.configSettings.options &&
            formCtrlDef.configSettings.options.hasOwnProperty("@Display") &&
            formCtrlDef.configSettings.options["@Display"].hasOwnProperty("addLabel")
        ) {
            var addItemElem = containingElem.find(
                ".complete-options-selection-container > .complete-options-footer > .complete-options-add"
            );
            var newText = formCtrlDef.configSettings.options["@Display"].addLabel;
            if (newText.charAt(newText.length - 1) !== "+") {
                newText += " +";
            }
            addItemElem.text(newText);
        }
        if (_.isPlainObject(formCtrlDef.configData) && !_.isEmpty(formCtrlDef.configData)) {
            //Update used/available based on configData
            self.setValue(formCtrlDef.configData);
        } else {
            _refreshSelectOptions();
            _setInitialReadOnlyState();
            loadedDeferred.resolve();
        }

        $scope.openCompleteChooser = function (evt) {
            evt.stopPropagation();
            _scope.selectList.renderer._showOptions();
        };
    };
    this.$compiledAndLinked.$inject = ["$scope"];

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
